import axios from 'axios';
import { baseURL, generalEndpoint } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || baseURL + '/' + generalEndpoint;

/**
 * Add a new Symbol
 * @param {string} token - Authentication token
 * @param {Object} symbolData - Symbol data object
 * @param {string} symbolData.name - Symbol name (required)
 * @param {string} symbolData.description - Symbol description
 * @param {number} symbolData.lpId - Liquidity Provider ID (required)
 * @param {string} symbolData.lpSymbolName - LP Symbol name
 * @param {number} symbolData.digits - Decimal digits
 * @param {number} symbolData.contractSize - Contract size
 * @param {number} symbolData.minLotSize - Minimum lot size
 * @param {number} symbolData.maxLotSize - Maximum lot size
 * @param {number} symbolData.lotStep - Lot step
 * @param {number} symbolData.tickSize - Tick size
 * @param {string} symbolData.baseCurrency - Base currency
 * @param {string} symbolData.quoteCurrency - Quote currency
 * @param {boolean} symbolData.isActive - Whether symbol is active
 * @param {string} symbolData.fixSymbol - FIX symbol
 * @param {string} symbolData.securityType - Security type
 * @returns {Promise<Object>} The API response
 */
export const addSymbol = async (token, symbolData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        
        const response = await axios.post(
            `${API_BASE_URL}/Symbols`,
            symbolData,
            config
        );
        
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing Symbol
 * @param {string} token - Authentication token
 * @param {number} symbolId - Symbol ID to update
 * @param {Object} symbolData - Symbol data object
 * @returns {Promise<Object>} The API response
 */
export const updateSymbol = async (token, symbolId, symbolData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        
        const response = await axios.put(
            `${API_BASE_URL}/Symbols/${symbolId}`,
            symbolData,
            config
        );
        
        
        // Handle 204 No Content response (successful update with no response body)
        if (response.status === 204) {
            return { success: true, message: 'Symbol updated successfully' };
        }
        
        return response.data;
    } catch (error) {
        
        // Provide more specific error information
        if (error.response?.status === 405) {
            throw new Error(`Method Not Allowed (405): The server does not support the ${error.config?.method?.toUpperCase()} method for this endpoint. Please check the API documentation.`);
        }
        
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a Symbol
 * @param {string} token - Authentication token
 * @param {number} symbolId - Symbol ID to delete
 * @returns {Promise<Object>} The API response
 */
export const deleteSymbol = async (token, symbolId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        
        const response = await axios.delete(
            `${API_BASE_URL}/Symbols/${symbolId}`,
            config
        );
        
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get all symbols for a specific LP
 * @param {string} token - Authentication token
 * @param {number} lpId - Liquidity Provider ID
 * @returns {Promise<Object>} The API response
 */
export const getSymbolsByLP = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
    };

    const startTime = Date.now();

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Symbols/lp/${lpId}`,
            config
        );
        
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (duration > 5000) {
            console.warn(`⚠️ Slow API response: ${duration}ms for /Symbols/lp/${lpId}`);
        }
        
        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (error.code === 'ECONNABORTED') {
            throw new Error(`Request timeout after ${duration}ms. The server is taking too long to respond.`);
        }
        
        throw error.response?.data || error.message;
    }
};

/**
 * Get all active symbols
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getActiveSymbols = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
    };

    const startTime = Date.now();
    
    try {
        const response = await axios.get(
            `${API_BASE_URL}/Symbols/active`,
            config
        );
        
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        
        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        
        throw error.response?.data || error.message;
    }
};

/**
 * Get all symbols
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getAllSymbols = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Symbols`,
            config
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};
/**
 * Get symbol information by symbol name
 * @param {string} symbolName - Symbol name to fetch information for
 * @returns {Promise<Object>} The API response
 */
export const getSymbolInfo = async (symbolName) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
        throw new Error('No authentication token found');
    }

    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        
        // First, get all symbols and find the one matching the symbol name
        const response = await axios.get(
            `${API_BASE_URL}/Symbols`,
            config
        );
        
        
        // Find the symbol that matches the symbolName
        const symbols = Array.isArray(response.data) ? response.data : [];
        const symbolInfo = symbols.find(symbol => 
            symbol.name === symbolName || 
            symbol.lpSymbolName === symbolName ||
            symbol.fixSymbol === symbolName
        );
        
        if (!symbolInfo) {
            throw new Error(`Symbol '${symbolName}' not found`);
        }
        
        return symbolInfo;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};


